/**
 * Per-conversation panneau ordering, persisted in localStorage.
 *
 * Server-side, panneaux come back sorted by `created_at`. The user can
 * drag to reorder; we persist the sequence here and re-apply it when
 * the SPA boots / after refetches. New panneaux not in the saved order
 * append at the end. Removed panneaux drop out silently.
 */
const KEY_PREFIX = "bunapp.panneau_order:";

function key(conv: string): string {
  return `${KEY_PREFIX}${conv}`;
}

export function loadOrder(conv: string): string[] {
  try {
    const raw = localStorage.getItem(key(conv));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function saveOrder(conv: string, ids: string[]): void {
  try {
    localStorage.setItem(key(conv), JSON.stringify(ids));
  } catch {
    /* private browsing — ephemeral */
  }
}

/**
 * Reorder a server-sorted list using a saved sequence. Items missing
 * from the saved sequence get appended in their original (server) order.
 */
export function applySavedOrder<T extends { id: string }>(
  items: T[],
  saved: string[],
): T[] {
  if (saved.length === 0) return items;
  const byId = new Map(items.map((p) => [p.id, p]));
  const out: T[] = [];
  for (const id of saved) {
    const found = byId.get(id);
    if (found) {
      out.push(found);
      byId.delete(id);
    }
  }
  // Append the rest in server order.
  for (const p of items) {
    if (byId.has(p.id)) {
      out.push(p);
      byId.delete(p.id);
    }
  }
  return out;
}
